import React, { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import moment from "moment";
import { rembursment } from "../../utils/Helpers";
import { createReimbursementRequest, getReimbursementRequest, uploadPdf } from "../../services/index";
import RembursmentList from "./Components/RembursmentList";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { Send, FileText, ChevronDown, User, CalendarDays, AlignLeft, CheckCircle2, Clock, UploadCloud, X, LayoutList, Banknotes } from "lucide-react";
import CustomSelect from "../../components/CustomSelect";
import DateBox from "../../components/DateBox";

const getTodayUTCDate = () => moment().utc().toDate();

const Rembursment = () => {
    const [selectedDate, setSelectedDate] = useState(getTodayUTCDate());
    const [selectedRequestType, setSelectedRequestType] = useState("");
    const [Reimbursements, setReimbursements] = useState([]);

    const userProfile = useSelector((state) => state.user.userinfo);
    const [offset, setOffset] = useState(0);
    const itemsPerPage = 10;

    const [amount, setAmount] = useState("");
    const [requestDetail, setRequestDetail] = useState("");
    const [pdfFile, setPdfFile] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const [errMsg, setErrMsg] = useState({ err: false, msg: "", color: "" });
    const [showDatePicker, setShowDatePicker] = useState(false);

    const notify = (isSuccess, message) => {
        if (isSuccess) {
            toast.success(message, {
                style: { className: "bg-white", color: "black" },
                position: "top-right",
            });
        } else {
            toast.error(message, {
                style: { className: "bg-white", color: "red" },
                position: "top-right",
            });
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === "application/pdf") {
            setPdfFile(file);
            setErrMsg({ err: false, msg: "", color: "" });
            e.target.value = null;
        } else if (file) {
            setErrMsg({ err: true, msg: "Please upload a valid PDF file", color: "red" });
        }
    };

    const removePdf = () => {
        setPdfFile(null);
        document.getElementById("pdfUpload").value = "";
    };

    const fetchReimbursementRequest = async (offset) => {
        try {
            const response = await getReimbursementRequest(
                userProfile.Id, "", "", "", "", offset, itemsPerPage
            );
            setReimbursements(response?.data?.Result?.RequestList || []);
        } catch (err) {
            console.error("Error fetching Reimbursements:", err);
        }
    };

    const handleSubmit = async () => {
        if (!selectedRequestType || !amount || !requestDetail.trim() || !selectedDate || !pdfFile) {
            setErrMsg({ err: true, msg: "All fields are required!", color: "red" });
            return;
        }

        if (pdfFile.size > 2 * 1024 * 1024) { // 2MB file size check
            setErrMsg({ err: true, msg: "File size exceeds 2MB limit!", color: "red" });
            return;
        }

        setIsLoading(true);
        setErrMsg({ err: false, msg: "", color: "" });

        try {
            const formData = new FormData();
            formData.append("file", pdfFile);

            const uploadResponse = await uploadPdf(formData);
            if (!uploadResponse.data?.Result?.filePath) {
                setErrMsg({ err: true, msg: "Failed to submit request", color: "red" });
                setIsLoading(false);
                return;
            }

            const requestModel = {
                RequestType: Number(selectedRequestType),
                RequstDetail: requestDetail,
                Date: moment(selectedDate).format("YYYY-MM-DD"),
                InvoiceAmount: parseFloat(amount),
                InvoicePath: uploadResponse.data?.Result?.filePath,
                Comment: "",
            };

            await createReimbursementRequest(requestModel);

            // Reset form
            setSelectedDate(getTodayUTCDate());
            setSelectedRequestType("");
            setAmount("");
            setRequestDetail("");
            setPdfFile(null);

            setErrMsg({ err: false, msg: "Request submitted successfully!", color: "green" });
            notify(true, "Request Submitted Successfully!");
            await fetchReimbursementRequest(0);

        } catch (error) {
            console.error("Upload Error:", error);
            setErrMsg({ err: true, msg: "Failed to submit request", color: "red" });
            notify(false, "Submission failed. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchReimbursementRequest(offset);
    }, [offset]);

    const requestTypeOptions = {
        "": "Select Document Type",
        ...Object.fromEntries(Object.entries(rembursment).map(([key, value]) => [key, value]))
    };

    return (
        <Layout>
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="px-4 py-8 max-w-7xl mx-auto"
            >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-blue-500/10 rounded-xl">
                            <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Medical Reimbursement</h2>
                            <p className="text-gray-500 dark:text-gray-400 text-sm">Submit and track your medical invoices</p>
                        </div>
                    </div>

                    {userProfile?.MemberRole == 2 && (
                        <Link
                            to="/AdminRembursment"
                            className="flex items-center gap-2 px-5 py-2.5 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 font-bold rounded-xl border border-blue-100 dark:border-blue-500/20 hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-all active:scale-95 shadow-sm hover:shadow-blue-500/10"
                        >
                            <LayoutList className="w-5 h-5" />
                            View All Reimbursements
                        </Link>
                    )}
                </div>

                <div className="bg-white dark:bg-gray-900/50 backdrop-blur-xl border border-gray-100 dark:border-blue-gray-900 p-6 sm:p-8 rounded-2xl shadow-xl shadow-gray-200/50 dark:shadow-none mb-10">

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Request Type */}
                        <div className="space-y-2 lg:col-span-1">
                            <label className="block text-sm text-gray-700 dark:text-gray-300 ms-1 flex items-center gap-2">
                                <FileText className="w-4 h-4 text-gray-400" />
                                Request Type
                            </label>
                            <CustomSelect
                                options={requestTypeOptions}
                                value={selectedRequestType}
                                onChange={(e) => setSelectedRequestType(e.target.value)}
                                placeholder="Select Document Type"
                                disabled={isLoading}
                            />
                        </div>

                        {/* Hospitality Date */}
                        <div className="space-y-2 lg:col-span-1">
                            <label className="block text-sm text-gray-700 dark:text-gray-300 ms-1 flex items-center gap-2">
                                <CalendarDays className="w-4 h-4 text-gray-400" />
                                Hospitality Date
                            </label>
                            <div className="relative group">
                                <button
                                    type="button"
                                    onClick={() => setShowDatePicker((p) => !p)}
                                    className="w-full flex items-center justify-between p-1.5 bg-gray-50 dark:bg-black/50 border border-gray-100 dark:border-blue-gray-900 rounded-xl text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500/20 outline-none transition-all cursor-pointer hover:bg-gray-100 dark:hover:bg-white/10 group shadow-sm dark:shadow-none"
                                    disabled={isLoading}
                                >
                                    <div className="flex items-center gap-2 flex-1">
                                        <div className="bg-white dark:bg-white/10 px-4 py-2 rounded-lg font-medium tracking-wide shadow-sm dark:shadow-none border border-gray-100 dark:border-transparent flex-1 text-center">
                                            {selectedDate ? moment(selectedDate).format("DD MMM YYYY") : "Select Date"}
                                        </div>
                                    </div>
                                    <div className="px-3 border-l border-gray-100 dark:border-white/10 ml-2">
                                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${showDatePicker ? 'rotate-180 text-blue-500' : ''}`} />
                                    </div>
                                </button>

                                <AnimatePresence>
                                    {showDatePicker && (
                                        <div className="absolute z-50 mt-2 left-0">
                                            <DateBox
                                                mode="single"
                                                initialStartDate={selectedDate}
                                                maxDate={new Date()}
                                                onApply={(date) => {
                                                    setSelectedDate(date);
                                                    setShowDatePicker(false);
                                                }}
                                                onClose={() => setShowDatePicker(false)}
                                            />
                                        </div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>

                        {/* Amount */}
                        <div className="space-y-2 lg:col-span-1">
                            <label className="block text-sm text-gray-700 dark:text-gray-300 ms-1 flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-gray-400 pb-0.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 8.25H9m6 3H9m3 6-3-3h1.5a3 3 0 1 0 0-6M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                                </svg>
                                Amount (Rs)
                            </label>
                            <input
                                type="number"
                                name="amount"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="w-full text-sm p-[13px] bg-gray-50 dark:bg-black/50 border border-gray-100 dark:border-blue-gray-900 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 outline-none transition-all duration-200 shadow-sm dark:shadow-none placeholder:text-gray-400"
                                placeholder="Enter amount..."
                                disabled={isLoading}
                            />
                        </div>

                        {/* Request Detail */}
                        <div className="space-y-2 md:col-span-2 lg:col-span-2 flex flex-col h-full">
                            <label className="block text-sm text-gray-700 dark:text-gray-300 ms-1 flex items-center gap-2 shrink-0">
                                <AlignLeft className="w-4 h-4 text-gray-400" />
                                Request Detail
                            </label>
                            <textarea
                                value={requestDetail}
                                onChange={(e) => setRequestDetail(e.target.value)}
                                className="w-full h-full flex-1 text-sm p-4 bg-gray-50 dark:bg-black/50 border border-gray-100 dark:border-blue-gray-900 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 outline-none transition-all duration-200 resize-none placeholder:text-gray-400 shadow-sm dark:shadow-none min-h-[142px]"
                                placeholder="Write your details carefully..."
                                disabled={isLoading}
                            />
                        </div>

                        {/* Upload PDF */}
                        <div className="space-y-2 lg:col-span-1 flex flex-col h-full">
                            <label className="block text-sm text-gray-700 dark:text-gray-300 ms-1 flex items-center gap-2 shrink-0">
                                <UploadCloud className="w-4 h-4 text-gray-400" />
                                Upload PDF
                            </label>

                            {!pdfFile ? (
                                <label className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-xl bg-gray-50 dark:bg-black/20 hover:bg-gray-100 dark:hover:bg-gray-800/50 hover:border-blue-500/50 transition-all cursor-pointer group p-6 h-full min-h-[142px]">
                                    <div className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-sm mb-3 group-hover:scale-110 transition-transform">
                                        <UploadCloud className="w-6 h-6 text-blue-500" />
                                    </div>
                                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Click to attach PDF</span>
                                    <span className="text-[11px] text-gray-400 mt-1 max-w-[200px] text-center">Combine all your medical invoices. Max 2MB.</span>
                                    <input
                                        type="file"
                                        id="pdfUpload"
                                        onChange={handleFileChange}
                                        accept="application/pdf"
                                        className="hidden"
                                        disabled={isLoading}
                                    />
                                </label>
                            ) : (
                                <div className="flex-1 flex flex-col items-center justify-center border border-green-200 dark:border-green-900/50 rounded-xl bg-green-50/50 dark:bg-green-900/10 p-6 relative h-full min-h-[142px]">
                                    <button
                                        onClick={removePdf}
                                        className="absolute top-3 right-3 p-1.5 bg-white dark:bg-gray-800 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 shadow-sm transition-all"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                    <div className="p-3 bg-green-100 dark:bg-green-800/50 rounded-full mb-3 text-green-600 dark:text-green-400">
                                        <CheckCircle2 className="w-6 h-6" />
                                    </div>
                                    <span className="text-sm font-bold text-green-700 dark:text-green-400 text-center w-[200px] truncate leading-tight">
                                        {pdfFile.name}
                                    </span>
                                    <span className="text-[11px] font-medium text-green-600/70 dark:text-green-400/70 mt-1">Ready to upload</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <AnimatePresence>
                        {errMsg.err && (
                            <motion.p
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                className={`text-xs mt-3 font-medium flex items-center gap-1.5 ms-1 ${errMsg.color === 'red' ? 'text-red-500' : 'text-green-500'}`}
                            >
                                <span className={`w-1 h-1 rounded-full ${errMsg.color === 'red' ? 'bg-red-500' : 'bg-green-500'}`} />
                                {errMsg.msg}
                            </motion.p>
                        )}
                    </AnimatePresence>

                    {/* Submit Button */}
                    <div className="flex justify-end mt-8 pt-6">
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleSubmit}
                            disabled={isLoading}
                            className="flex items-center gap-2 px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-500/20 transition-all disabled:opacity-70 disabled:cursor-not-allowed group w-full sm:w-auto justify-center"
                        >
                            {isLoading ? (
                                <>
                                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    <span>Submitting...</span>
                                </>
                            ) : (
                                <>
                                    <span>Submit Request</span>
                                    <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                </>
                            )}
                        </motion.button>
                    </div>
                </div>

                {/* List Container */}
                <RembursmentList Reimbursements={Reimbursements} itemsPerPage={itemsPerPage} setOffset={setOffset} />

            </motion.div>
        </Layout>
    );
};

export default Rembursment;